import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, List, Modal, Space, Spin, Switch, Typography, message } from 'antd';
import { FileOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useAuth } from '../common/security/AuthContext';
import { dccSearch, type DccSearchItem } from '../../services/dccApi';
import FilePreviewModal, { canPreviewFile } from '../common/FilePreviewModal';

const { Text } = Typography;

export interface FolderFileSearchModalProps {
  open: boolean;
  value?: any;
  rootPath?: string;
  returnOption?: 'only_folder' | 'direct_path';
  returnAllModes?: boolean;
  onOk?: (val: any) => void;
  onCancel: () => void;
  onSelect?: (args: { fullPath: string; name: string }) => void;
}

type Row = {
  key: string;
  name: string;
  path: string;
  isFolder: boolean;
};

const normalizePath = (p: string): string => {
  return String(p || '')
    .trim()
    .replace(/^[/\\]+/, '')
    .replace(/^root[\\/]/i, '')
    .replace(/\\/g, '/')
    .replace(/\/+$/, '');
};

const mapRow = (it: DccSearchItem): Row => {
  const name = String(it?.name || '').replace(/\\/g, '/');
  const path = String(it?.path || name || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
  return {
    key: path || name,
    name,
    path,
    isFolder: Boolean(it?.isFolder),
  };
};

const FolderFileSearchModal: React.FC<FolderFileSearchModalProps> = ({
  open,
  rootPath = '',
  returnOption = 'direct_path',
  returnAllModes = false,
  onOk,
  onCancel,
  onSelect,
}) => {
  const { token } = useAuth();

  const [qName, setQName] = useState('');
  const [qPath, setQPath] = useState('');
  const [includeSubfolders, setIncludeSubfolders] = useState(true);

  const [items, setItems] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPath, setPreviewPath] = useState<string | null>(null);

  const effectiveRoot = useMemo(() => normalizePath(rootPath), [rootPath]);

  const scopedItems = useMemo(() => {
    if (includeSubfolders) return items;
    const root = String(effectiveRoot || '').replace(/\/+$/, '');
    if (!root) {
      return items.filter((it) => {
        const p = String(it.path || '').replace(/\/+$/, '');
        return p.length > 0 && !p.includes('/');
      });
    }
    return items.filter((it) => {
      const p = String(it.path || '').replace(/\/+$/, '');
      if (p === root) return true;
      if (!p.startsWith(`${root}/`)) return false;
      const rest = p.slice(root.length + 1);
      return !rest.includes('/');
    });
  }, [effectiveRoot, includeSubfolders, items]);

  useEffect(() => {
    if (!open) return;
    setQName('');
    setQPath('');
    setIncludeSubfolders(true);
    setItems([]);
    setSelectedKey(null);
    setIsPreviewOpen(false);
    setPreviewPath(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!token) return;

    const name = String(qName || '').trim();
    const path = String(qPath || '').trim();

    if (name.length < 2 && path.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const handle = window.setTimeout(() => {
      if (cancelled) return;
      setLoading(true);
      (async () => {
        try {
          const res = await dccSearch(
            {
              q: name || path,
              qName: name,
              qPath: path,
              root: effectiveRoot,
              limit: 300,
              useIndex: true,
            },
            token,
            controller.signal
          );
          if (cancelled) return;
          const mapped = (res.items || []).map(mapRow);
          setItems(mapped);
        } catch (e: any) {
          if (cancelled) return;
          if (e?.name === 'AbortError') return;
          message.error(e?.message || 'Search failed');
          setItems([]);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 400);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(handle);
    };
  }, [open, token, qName, qPath, effectiveRoot]);

  useEffect(() => {
    if (!open) return;
    if (selectedKey && scopedItems.some((x) => x.key === selectedKey)) return;
    setSelectedKey(scopedItems.length ? scopedItems[0].key : null);
  }, [open, scopedItems, selectedKey]);

  const selectedRow = useMemo(() => {
    if (!selectedKey) return null;
    return scopedItems.find((x) => x.key === selectedKey) || null;
  }, [scopedItems, selectedKey]);

  const getReturnPath = (row: Row | null): string | null => {
    if (!row) return null;
    const p = String(row.path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
    if (!p) return null;
    if (returnOption === 'direct_path') return p;
    // only_folder
    if (row.isFolder) return p;
    const idx = p.lastIndexOf('/');
    return idx >= 0 ? p.slice(0, idx) : '';
  };

  const getReturnPathForMode = (row: Row | null, mode: 'only_folder' | 'direct_path'): string | null => {
    if (!row) return null;
    const p = String(row.path || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
    if (!p) return null;
    if (mode === 'direct_path') return p;
    if (row.isFolder) return p;
    const idx = p.lastIndexOf('/');
    return idx >= 0 ? p.slice(0, idx) : '';
  };

  const handleConfirm = (row: Row | null) => {
    if (!row) return;
    onSelect?.({ fullPath: row.path, name: row.name });
    if (returnAllModes) {
      onOk?.({
        type: 'dcc_path',
        selected: row,
        modes: {
          direct_path: getReturnPathForMode(row, 'direct_path'),
          only_folder: getReturnPathForMode(row, 'only_folder'),
        },
      });
    } else {
      onOk?.(getReturnPath(row));
    }
  };

  const handlePreview = (row: Row) => {
    if (row.isFolder) return;
    setPreviewPath(row.path);
    setIsPreviewOpen(true);
  };

  return (
    <>
      <Modal
        title="Search files"
        open={open}
        onCancel={onCancel}
        width={820}
        destroyOnClose
        okText="Select"
        onOk={() => {
          handleConfirm(selectedRow);
          if (selectedRow) onCancel();
        }}
        okButtonProps={{ disabled: !selectedRow }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Space size={8} style={{ width: '100%' }}>
            <Input
              allowClear
              value={qName}
              onChange={(e) => setQName(e.target.value)}
              placeholder="Search by name (min 2 chars)"
              style={{ width: 220 }}
            />
            <Input
              allowClear
              value={qPath}
              onChange={(e) => setQPath(e.target.value)}
              placeholder={includeSubfolders ? 'Search by path (min 2 chars)' : 'Path search disabled'}
              disabled={!includeSubfolders}
              style={{ width: 220 }}
            />
            <Space size={6} align="center">
              <Text type="secondary">Include subfolders</Text>
              <Switch size="small" checked={includeSubfolders} onChange={setIncludeSubfolders} />
            </Space>
          </Space>

          <Spin spinning={loading}>
            <List<Row>
              bordered
              size="small"
              dataSource={scopedItems}
              locale={{ emptyText: 'No results' }}
              style={{ maxHeight: 420, overflow: 'auto' }}
              renderItem={(row) => {
                const active = row.key === selectedKey;
                const previewable = !row.isFolder && canPreviewFile(row.name);
                return (
                  <List.Item
                    style={{
                      cursor: 'pointer',
                      background: active ? '#e6f7ff' : undefined,
                      userSelect: 'none',
                    }}
                    onClick={() => setSelectedKey(row.key)}
                    onDoubleClick={() => {
                      setSelectedKey(row.key);
                      if (previewable) {
                        handlePreview(row);
                        return;
                      }
                      handleConfirm(row);
                      onCancel();
                    }}
                  >
                    <Space size={8} style={{ minWidth: 0 }}>
                      {row.isFolder ? <FolderOpenOutlined /> : <FileOutlined />}
                      <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
                        <Text strong ellipsis={{ tooltip: row.name }} style={previewable ? { color: '#1677ff' } : undefined}>
                          {row.name}
                        </Text>
                      </Space>
                    </Space>
                  </List.Item>
                );
              }}
            />
          </Spin>
        </Space>
      </Modal>

      <FilePreviewModal
        open={isPreviewOpen}
        path={previewPath}
        token={token}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewPath(null);
        }}
      />
    </>
  );
};

export default FolderFileSearchModal;
